// ============================================================
// Valorant regions
// ============================================================
export const VALORANT_REGIONS = ['eu', 'na', 'ap', 'kr', 'latam', 'br'] as const;
export type ValorantRegion = (typeof VALORANT_REGIONS)[number];

// ============================================================
// Skin tiers (редкости) — UUID'ы из valorant-api.com/v1/contenttiers
// ============================================================
export const SKIN_TIERS = {
  SELECT: '12683d76-48d7-84a3-4e09-6985794f0445',     // Select (зелёный)
  DELUXE: '0cebb8be-46d7-c12a-d306-e9907bfc5a25',     // Deluxe (синий)
  PREMIUM: '60bca009-4182-7998-dee7-b8a2558dc369',    // Premium (фиолетовый)
  EXCLUSIVE: 'e046854e-406c-37f4-6607-19a9ba8426fc',  // Exclusive (красный)
  ULTRA: '411e4a55-4e59-7757-41f0-86a53f101bb5',      // Ultra (жёлтый)
} as const;

export type SkinTierName = keyof typeof SKIN_TIERS;

export interface ContentTier {
  uuid: string;
  displayName: string;
  devName: string;
  rank: number;
  juiceValue: number;
  juiceCost: number;
  highlightColor: string;
  displayIcon: string;
}

// ============================================================
// Skins
// ============================================================
export interface SkinChroma {
  uuid: string;
  displayName: string;
  displayIcon: string | null;
  fullRender: string | null;
  swatch: string | null;
  streamedVideo: string | null;
}

export interface SkinLevel {
  uuid: string;
  displayName: string;
  levelItem: string | null;
  displayIcon: string | null;
  streamedVideo: string | null;
}

export interface WeaponSkin {
  uuid: string;
  displayName: string;
  themeUuid: string | null;
  contentTierUuid: string | null;
  displayIcon: string | null;
  wallpaper: string | null;
  assetPath: string;
  chromas: SkinChroma[];
  levels: SkinLevel[];
  /** Добавленные сервером поля */
  weaponUuid?: string;
  weaponName?: string;
  price?: number;
}

export interface Weapon {
  uuid: string;
  displayName: string;
  category: string;
  displayIcon: string;
  skins: WeaponSkin[];
}

// ============================================================
// Store offers
// ============================================================
export interface StoreOffer {
  /** UUID скина (LevelID первого уровня) — им оперирует storefront */
  offerId: string;
  /** Полный объект скина из кэша */
  skin: WeaponSkin | null;
  /** Цена в VP */
  cost: number;
  /** Название оружия */
  weaponName: string;
}

export interface DailyStore {
  offers: StoreOffer[];
  /** Секунд до обновления */
  remainingSeconds: number;
}

export interface BonusStore {
  offers: Array<StoreOffer & { discountPercent: number; discountedCost: number }>;
  remainingSeconds: number;
}

export interface Storefront {
  daily: DailyStore;
  bonus: BonusStore | null;
  fetchedAt: number;
}

// ============================================================
// User / Favorites
// ============================================================
export interface User {
  telegramId: number;
  riotPuuid: string | null;
  riotRegion: ValorantRegion | null;
  hasTokens: boolean;
  createdAt: number;
}

export interface FavoriteSkin {
  skinUuid: string;
  addedAt: number;
  /** Если в истории был — последняя дата появления */
  lastAppearedAt: number | null;
}

// ============================================================
// API payloads
// ============================================================
export interface LoginRequest {
  /** initData из Telegram SDK */
  initData: string;
  /** Riot credentials */
  username: string;
  password: string;
  region: ValorantRegion;
  /** 2FA код, если Riot попросил */
  multifactorCode?: string;
}

export interface LoginResponse {
  success: boolean;
  /** Есть ли запрос на 2FA (cookie стейт сохранён на сервере по session id) */
  requireMultifactor?: boolean;
  multifactorEmail?: string;
  sessionId?: string;
  user?: User;
}

export interface ApiError {
  error: string;
  code?: string;
  details?: unknown;
}

export type ApiResponse<T> = { data: T } | ApiError;

// ============================================================
// WebSocket messages (Durable Object)
// ============================================================
export type WsMessage =
  | { type: 'store_update'; payload: Storefront }
  | { type: 'favorite_hit'; payload: { skinUuid: string; skinName: string } }
  | { type: 'auth_required' }
  | { type: 'ping' }
  | { type: 'pong' };
